/* =============================================================
   Desktop navbar: open dropdowns on hover.

   Dawn's header dropdowns are <details>/<summary>, so they only open on
   click. This opens them on pointer hover instead, while leaving click,
   keyboard and touch exactly as they were.

   It drives the same state Dawn's own HeaderMenu uses, so the existing
   transition, caret rotation and styling all apply unchanged:

     open        the <details> `open` attribute
     .menu-opening   the class base.css animates the submenu on
     aria-expanded   on the <summary>

   Two things it deliberately does NOT do:

   - No trapFocus. Dawn calls that on click because a click is a
     deliberate act; trapping focus on mere hover would yank the caret
     out of whatever the visitor was doing.
   - Nothing below 990px. There the header is a drawer, hover does not
     exist on touch, and Dawn's own drawer logic owns the same elements.
   ============================================================= */

(function () {
  'use strict';

  var DESKTOP = '(min-width: 990px)';
  var CLOSE_DELAY = 120;

  function isDesktop() {
    return window.matchMedia && window.matchMedia(DESKTOP).matches;
  }

  function open(details) {
    var summary = details.querySelector('summary');
    if (!summary || details.hasAttribute('open')) return;

    details.setAttribute('open', '');
    /* Next frame, so the submenu has a layout before the class that
       transitions it lands — setting both together skips the animation. */
    requestAnimationFrame(function () {
      details.classList.add('menu-opening');
    });
    summary.setAttribute('aria-expanded', 'true');
  }

  /* Shut every other open dropdown at once. Without this, moving along the
     bar left the previous panel mid-fade while the next one opened, so two
     overlapped for a moment — "About Us" showing through "School
     Solutions". A menubar only ever has one panel open. */
  function closeOthers(except) {
    Array.prototype.forEach.call(
      document.querySelectorAll('.header__inline-menu details[open]'),
      function (other) {
        if (other !== except) close(other, true);
      }
    );
  }

  function close(details, immediate) {
    var summary = details.querySelector('summary');
    if (!details.hasAttribute('open')) return;

    details.classList.remove('menu-opening');
    if (summary) summary.setAttribute('aria-expanded', 'false');

    /* immediate: collapse now rather than after the fade. Used when another
       dropdown is taking over, where the fade is what causes the overlap. */
    if (immediate) {
      details.removeAttribute('open');
      return;
    }

    /* Let the fade finish before collapsing, otherwise the submenu
       vanishes instantly instead of animating out. */
    var submenu = details.querySelector('.header__submenu');
    if (!submenu) {
      details.removeAttribute('open');
      return;
    }

    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      submenu.removeEventListener('transitionend', finish);
      /* Guard against a re-open during the fade: if the pointer came
         back, menu-opening is set again and the menu should stay. */
      if (!details.classList.contains('menu-opening')) details.removeAttribute('open');
    };

    submenu.addEventListener('transitionend', finish);
    setTimeout(finish, 400);
  }

  function bind(details) {
    if (details.dataset.hoverBound === 'true') return;
    details.dataset.hoverBound = 'true';

    var timer = null;

    details.addEventListener('mouseenter', function () {
      if (!isDesktop()) return;
      clearTimeout(timer);
      closeOthers(details);
      open(details);
    });

    details.addEventListener('mouseleave', function () {
      if (!isDesktop()) return;
      /* Small grace period: the gap between the trigger and the panel
         would otherwise close the menu as the pointer crosses it. */
      clearTimeout(timer);
      timer = setTimeout(function () { close(details); }, CLOSE_DELAY);
    });

    /* A click still toggles — keyboard and touch keep working, and a
       click on an already hover-opened menu closes it as expected. */
    details.addEventListener('focusout', function (event) {
      if (!isDesktop()) return;
      if (!details.contains(event.relatedTarget)) close(details);
    });
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(
      scope.querySelectorAll('.header__inline-menu details'),
      bind
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor re-renders the header section on every setting change. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
